import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import type { User, Lead, PropertyType } from '../types';
import { PROPERTY_TYPE_LABELS } from '../types';
import { formatDateTime } from '../utils/date';
import { Layout } from '../components/Layout';
import { RecordingPlayer } from '../components/RecordingPlayer';

export function LeadDetail() {
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState<User | null>(null);
  const [lead, setLead] = useState<Lead | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusReason, setStatusReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const navigate = useNavigate();

  const getApiBase = () => {
    return '/api';
  };

  const request = async (endpoint: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('rewagent_token');
    const response = await fetch(`${getApiBase()}${endpoint}`, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers,
      },
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Request failed');
    return data;
  };

  useEffect(() => {
    const stored = localStorage.getItem('rewagent_user');
    if (stored) setUser(JSON.parse(stored));
    else navigate('/');
  }, [navigate]);

  useEffect(() => {
    if (user && id) fetchLead();
  }, [user, id]);

  const fetchLead = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await request(`/leads/${id}`);
      setLead(data.lead);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load lead');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try { await request('/auth/logout', { method: 'POST' }); } catch (err) { console.error(err); }
    localStorage.removeItem('rewagent_user');
    localStorage.removeItem('rewagent_token');
    navigate('/');
  };

  const handleUpdateStatus = async () => {
    if (!newStatus) return;
    setActionLoading(true);
    try {
      const result = await request(`/leads/${id}/status`, {
        method: 'POST',
        body: JSON.stringify({ status: newStatus, reason: statusReason || undefined }),
      });

      // Update cached user balance if returned
      if (result.balance !== undefined) {
        const storedUser = localStorage.getItem('rewagent_user');
        if (storedUser) {
          const updatedUser = { ...JSON.parse(storedUser), balance: result.balance };
          localStorage.setItem('rewagent_user', JSON.stringify(updatedUser));
          setUser(updatedUser);
        }
      }

      setShowStatusModal(false);
      fetchLead();
    } catch (err) {
      alert('Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'qualified': return 'bg-green-100 text-green-800 border-green-200';
      case 'disqualified': return 'bg-red-100 text-red-800 border-red-200';
      case 'new': return 'bg-[#f3ede3] text-[#1a1a1a] border-[#e3dbcf]';
      default: return 'bg-[#fcfbfa] text-[#6b635a] border-[#e3dbcf]';
    }
  };

  const getScoreColor = (score: number | null) => {
    if (!score) return 'text-[#b8b1a5]';
    if (score >= 7) return 'text-[#1a1a1a] font-bold';
    if (score >= 4) return 'text-[#6b635a] font-semibold';
    return 'text-[#8c8578]';
  };

  const getPropertyTypeLabel = (type: PropertyType) => PROPERTY_TYPE_LABELS[type] || type;

  const formatPrice = (val: number | null) => val ? `$${Number(val).toLocaleString()}` : 'N/A';

  const getOccupancyLabel = (occ: number | null) => {
    if (occ === null) return 'N/A';
    const labels = ['Vacant', 'Occupied by Owner', 'Occupied by Tenant', 'Unknown'];
    return labels[occ] || 'N/A';
  };

  if (!user) return null;

  return (
    <Layout showHeader userName={user.name} onLogout={handleLogout}>
      <div className="min-h-screen bg-[#f3ede3] font-[Inter]">
        <main className="pt-6 pb-12 px-6 lg:px-8 max-w-[90rem] mx-auto">
          {/* Back link */}
          <Link to="/dashboard" className="inline-flex items-center text-[#8c8578] hover:text-[#1a1a1a] mb-3 transition-colors uppercase tracking-widest text-xs font-bold">
            <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Dashboard
          </Link>

          {loading ? (
            <div className="flex items-center justify-center py-24">
              <svg className="animate-spin h-10 w-10 text-[#1a1a1a]" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            </div>
          ) : error ? (
            <div className="text-center py-20 bg-[#fcfbfa] border border-[#e3dbcf] rounded-2xl shadow-sm">
              <p className="text-red-600 mb-6">{error}</p>
              <Link to="/dashboard" className="text-[#1a1a1a] font-bold uppercase tracking-widest text-sm hover:underline">Return to Dashboard</Link>
            </div>
          ) : lead ? (
            <>
              {/* Lead Header — compact */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4 pb-4 border-b border-[#e3dbcf]">
                <div>
                  <p className="text-[#8c8578] font-bold tracking-widest text-xs uppercase mb-0.5">Lead ID: {lead.lead_code}</p>
                  <h1 className="text-3xl font-bold text-[#1a1a1a] tracking-tight">
                    {`${lead.owner_firstname} ${lead.owner_lastname}`}
                    <span className="text-[#6b635a] text-base font-normal ml-3">{lead.prop_address}</span>
                  </h1>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={`inline-flex px-4 py-1.5 rounded text-sm font-bold tracking-widest border uppercase ${getStatusBadgeClass(lead.status)}`}>
                    {lead.status}
                  </span>
                  {lead.status !== 'qualified' && (
                    <button
                      onClick={() => { setShowStatusModal(true); setNewStatus(lead.status); setStatusReason(''); }}
                      className="px-4 py-1.5 bg-[#1a1a1a] text-[#fcfbfa] hover:bg-[#000000] border border-[#1a1a1a] rounded text-sm font-bold tracking-widest uppercase transition-all shadow-sm"
                    >
                      Modify Status
                    </button>
                  )}
                </div>
              </div>

              {lead.disqualify_reason && (
                <div className="mb-10 p-6 bg-[#fcfbfa] border border-[#e3dbcf] rounded-xl flex items-start gap-4 shadow-sm">
                  <svg className="w-6 h-6 text-[#8c8578] shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <span className="text-[#8c8578] text-xs font-bold uppercase tracking-widest block mb-1">Disqualification Insight</span>
                    <p className="text-[#1a1a1a] text-sm italic">"{lead.disqualify_reason}"</p>
                  </div>
                </div>
              )}

              {/* Two-Column Layout */}
              <div className="flex flex-col lg:flex-row gap-16">
                {/* Left Column: General Notes + Recording */}
                <div className="lg:w-1/2 space-y-8">
                  {/* General Notes */}
                  {lead.info && (
                    <div>
                      <h2 className="text-sm font-bold text-[#8c8578] uppercase tracking-widest mb-5">General Notes</h2>
                      <div className="bg-[#fcfbfa] border border-[#e3dbcf] rounded-2xl p-10 shadow-sm">
                        <p className="text-[#1a1a1a] text-base leading-relaxed whitespace-pre-wrap">{lead.info}</p>
                      </div>
                    </div>
                  )}

                  {/* Recording Player */}
                  {(lead as any).has_recording === 1 && (
                    <RecordingPlayer
                      leadId={lead.id}
                      apiBase={getApiBase()}
                      routePrefix="/leads"
                      authHeaders={{
                        ...(localStorage.getItem('rewagent_token') ? { Authorization: `Bearer ${localStorage.getItem('rewagent_token')}` } : {}),
                      }}
                    />
                  )}
                </div>

                {/* Right Column: Lead Details */}
                <div className="lg:w-1/2">
                  <div className="bg-[#fcfbfa] border border-[#e3dbcf] rounded-2xl p-10 md:p-12 shadow-sm">
                    {/* Owner Info */}
                    <div>
                      <h2 className="text-sm font-bold text-[#8c8578] uppercase tracking-widest mb-6">Owner Information</h2>
                      <div className="space-y-5">
                        <div className="flex justify-between border-b border-[#f3ede3] pb-4">
                          <span className="text-[#6b635a] text-base font-medium">Name</span>
                          <span className="text-[#1a1a1a] text-base font-bold">{`${lead.owner_firstname} ${lead.owner_lastname}`}</span>
                        </div>
                        <div className="flex justify-between border-b border-[#f3ede3] pb-4">
                          <span className="text-[#6b635a] text-base font-medium">Phone</span>
                          {lead.status === 'qualified' ? (
                            <span className="text-[#1a1a1a] text-base font-bold">{lead.owner_phone}</span>
                          ) : (
                            <span className="text-[#8c8578] text-base font-bold italic">Hidden (Lead Not Qualified)</span>
                          )}
                        </div>
                        {lead.owner_email && (
                          <div className="flex justify-between pb-1">
                            <span className="text-[#6b635a] text-base font-medium">Email</span>
                            {lead.status === 'qualified' ? (
                              <span className="text-[#1a1a1a] text-base font-bold">{lead.owner_email}</span>
                            ) : (
                              <span className="text-[#8c8578] text-base font-bold italic">Hidden (Lead Not Qualified)</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <hr className="my-8 border-[#e3dbcf]" />

                    {/* Property Details */}
                    <div>
                      <h2 className="text-sm font-bold text-[#8c8578] uppercase tracking-widest mb-6">Property Details</h2>
                      <div className="space-y-5">
                        <div className="flex justify-between border-b border-[#f3ede3] pb-4">
                          <span className="text-[#6b635a] text-base font-medium">Address</span>
                          <span className="text-[#1a1a1a] text-base font-bold text-right ml-4">{lead.prop_address}</span>
                        </div>
                        <div className="flex justify-between border-b border-[#f3ede3] pb-4">
                          <span className="text-[#6b635a] text-base font-medium">Type</span>
                          <span className="text-[#1a1a1a] text-base font-bold">{getPropertyTypeLabel(lead.prop_type)}</span>
                        </div>
                        <div className="flex justify-between border-b border-[#f3ede3] pb-4">
                          <span className="text-[#6b635a] text-base font-medium">Occupancy</span>
                          <span className="text-[#1a1a1a] text-base font-bold">{getOccupancyLabel(lead.occupancy)}</span>
                        </div>
                        <div className="flex justify-between pb-1">
                          <span className="text-[#6b635a] text-base font-medium">Condition</span>
                          <span className="text-[#1a1a1a] text-base font-bold">{lead.cond_scale ? `${lead.cond_scale}/10` : 'N/A'}</span>
                        </div>
                      </div>
                    </div>

                    <hr className="my-8 border-[#e3dbcf]" />

                    {/* Financial Info */}
                    <div>
                      <h2 className="text-sm font-bold text-[#8c8578] uppercase tracking-widest mb-6">Financial Information</h2>
                      <div className="space-y-5">
                        <div className="flex justify-between border-b border-[#f3ede3] pb-4">
                          <span className="text-[#6b635a] text-base font-medium">Market Value</span>
                          <span className="text-[#1a1a1a] text-lg font-bold">{formatPrice(lead.market_val)}</span>
                        </div>
                        <div className="flex justify-between pb-1">
                          <span className="text-[#6b635a] text-base font-medium">Asking Price</span>
                          <span className="text-[#1a1a1a] text-lg font-bold">{formatPrice(lead.ask_price)}</span>
                        </div>
                      </div>
                    </div>

                    <hr className="my-8 border-[#e3dbcf]" />

                    {/* Sale Info */}
                    <div>
                      <h2 className="text-sm font-bold text-[#8c8578] uppercase tracking-widest mb-6">Sale Information</h2>
                      <div className="space-y-5">
                        <div className="flex justify-between border-b border-[#f3ede3] pb-4">
                          <span className="text-[#6b635a] text-base font-medium">Motivation</span>
                          <span className="text-[#1a1a1a] text-base font-bold">{lead.motivation ? `${lead.motivation}/10` : 'N/A'}</span>
                        </div>
                        <div className="flex justify-between border-b border-[#f3ede3] pb-4">
                          <span className="text-[#6b635a] text-base font-medium">Lead Score</span>
                          <span className={`text-base font-bold ${getScoreColor(lead.lead_score)}`}>
                            {lead.lead_score || 0}<span className="text-[#b8b0a5] font-medium text-sm ml-0.5">/10</span>
                          </span>
                        </div>
                        {user?.role !== 'qa' && (
                          <div className="flex justify-between border-b border-[#f3ede3] pb-4">
                            <span className="text-[#6b635a] text-base font-medium">Lead Cost</span>
                            <span className="text-[#1a1a1a] text-lg font-bold">{formatPrice(lead.lead_cost)}</span>
                          </div>
                        )}
                        <div className="flex justify-between pb-1">
                          <span className="text-[#6b635a] text-base font-medium">Campaign</span>
                          <span className="text-[#1a1a1a] text-base font-bold">{lead.campaign_name || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>


              {/* Meta */}
              <div className="text-xs font-bold text-[#8c8578] uppercase tracking-widest text-center mt-10">
                Ref: {id} • Created: {formatDateTime(lead.created_at)} • Synchronized: {formatDateTime(lead.updated_at)}
              </div>
            </>
          ) : null}
        </main>

        {/* Status Modal */}
        {showStatusModal && (
          <div className="fixed inset-0 bg-[#1a1a1a]/40 backdrop-blur-md flex items-center justify-center z-50 p-4">
            <div className="bg-[#fcfbfa] border border-[#e3dbcf] rounded-2xl p-10 max-w-md w-full shadow-2xl">
              <h3 className="text-2xl font-bold text-[#1a1a1a] mb-2 tracking-tight">Update Status</h3>
              <p className="text-[#6b635a] text-sm mb-8">Choose the appropriate classification for this lead.</p>

              <div className="space-y-6 mb-10">
                <div>
                  <label className="text-xs font-bold text-[#8c8578] uppercase tracking-widest block mb-3">Classification</label>
                  <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)}
                    className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-5 py-4 font-semibold focus:outline-none focus:border-[#1a1a1a] transition-all">
                    <option value="new">New</option>
                    <option value="qualified">Qualified</option>
                    <option value="disqualified">Disqualified</option>
                  </select>
                </div>

                {newStatus === 'disqualified' && (
                  <div>
                    <label className="text-xs font-bold text-[#8c8578] uppercase tracking-widest block mb-3">Reason for Disqualification</label>
                    <textarea value={statusReason} onChange={(e) => setStatusReason(e.target.value)} rows={4}
                      placeholder="Please provide specific reasoning..."
                      className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-5 py-4 focus:outline-none focus:border-[#1a1a1a] transition-all placeholder:text-[#b8b0a5]" />
                  </div>
                )}
              </div>

              <div className="flex gap-4">
                <button onClick={handleUpdateStatus} disabled={actionLoading || (newStatus === 'disqualified' && !statusReason)}
                  className="flex-1 py-4 bg-[#1a1a1a] hover:bg-[#000000] text-[#fcfbfa] font-bold uppercase tracking-widest rounded transition-all disabled:opacity-30">
                  {actionLoading ? 'Saving...' : 'Confirm'}
                </button>
                <button onClick={() => setShowStatusModal(false)} className="flex-1 py-4 bg-[#f3ede3] hover:bg-[#e3dbcf]/50 text-[#1a1a1a] font-bold uppercase tracking-widest rounded transition-all">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
